/* global React, Button, Icon */
const PUBLIC_INVOICE_PATH_RE = /^\/invoice\/view\/([^/]+)\/([^/]+)\/([^/]+)\/?$/;

const publicInvoiceConfig = () => window.PLUMME_FIREBASE_CONFIG || {
  projectId: 'zaldo-lite',
  functionsRegion: 'europe-west1',
};

const publicFunctionUrl = (name) => {
  const config = publicInvoiceConfig();
  const rawOrigin = String(window.PLUMME_FIREBASE_FUNCTIONS_ORIGIN || config.functionsOrigin || '').trim();
  const origin = !rawOrigin || rawOrigin.includes('%VITE_') || rawOrigin.includes('__PLUMME_')
    ? ''
    : rawOrigin.replace(/\/+$/, '');
  return origin
    ? `${origin}/${name}`
    : `https://${config.functionsRegion || 'europe-west1'}-${config.projectId}.cloudfunctions.net/${name}`;
};

const invoicePublicPost = async (functionName, body) => {
  const response = await fetch(publicFunctionUrl(functionName), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.message || payload?.error || `HTTP ${response.status}`);
  }
  return payload;
};

const parsePublicInvoicePath = () => {
  const match = window.location.pathname.match(PUBLIC_INVOICE_PATH_RE);
  if (!match) return null;
  return {
    businessId: decodeURIComponent(match[1]),
    invoiceId: decodeURIComponent(match[2]),
    token: decodeURIComponent(match[3]),
  };
};

const publicNum = (value, fallback = 0) => {
  return window.PlummeMoneyInput.number(value, fallback);
};

const publicLineType = (line) => String(line?.lineType || line?.documentLineType || 'product');
const publicLineHasAmount = (line) => publicLineType(line) === 'product';
const publicLineNet = (line) => publicLineHasAmount(line) ? publicNum(line?.quantity, 1) * publicNum(line?.documentUnitPrice ?? line?.unitPrice) : 0;
const publicLineGross = (line) => {
  const net = publicLineNet(line);
  const vatRate = publicNum(line?.vatRate);
  const vatTreatment = line?.vatTreatment || (vatRate === 0 ? 'zeroRated' : 'standard');
  return vatTreatment === 'standard' ? net * (1 + vatRate / 100) : net;
};
const publicMoney = (value, currency = 'NOK') =>
  new Intl.NumberFormat(PlummeI18n.intlLocale(), { style: 'currency', currency, maximumFractionDigits: 2 }).format(publicNum(value));

function InvoicePublicView() {
  const route = parsePublicInvoicePath();
  const [state, setState] = React.useState({ loading: true, error: '', invoice: null, settings: null });
  const [accepting, setAccepting] = React.useState(false);
  const [acceptedMessage, setAcceptedMessage] = React.useState('');

  React.useEffect(() => {
    let stopped = false;
    if (!route) {
      setState({ loading: false, error: 'Lenken er ugyldig.', invoice: null, settings: null });
      return () => {};
    }
    invoicePublicPost('invoicePublicViewHttp', route)
      .then((payload) => {
        if (stopped) return;
        setState({ loading: false, error: '', invoice: payload.invoice || null, settings: payload.settings || null });
      })
      .catch((error) => {
        if (stopped) return;
        setState({ loading: false, error: error?.message || 'Kunne ikke åpne dokumentet.', invoice: null, settings: null });
      });
    return () => { stopped = true; };
  }, [window.location.pathname]);

  const invoice = state.invoice || {};
  const settings = state.settings || {};
  const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
  const netTotal = lines.reduce((sum, line) => sum + publicLineNet(line), 0);
  const grossTotal = lines.reduce((sum, line) => sum + publicLineGross(line), 0);
  // Ordene og fortegnet dokumenttypen skal ha ligger i
  // `src/services/invoicePublicDocument.js`, ikke her: en kreditnota er ikke en
  // faktura, og reglene for det er testbare for seg.
  const doc = window.PlummeInvoicePublicDocument.presentation(invoice);
  const isOffer = doc.isOffer;
  const isCredit = doc.isCredit;
  const alreadyAccepted = Boolean(invoice.offerAcceptedAt || acceptedMessage);
  const documentLabel = doc.documentLabel;
  const pdfUrl = invoice.pdfDownloadUrl || invoice.pdf?.downloadUrl || '';
  const amountOf = (value) => window.PlummeInvoicePublicDocument.displayAmount(invoice, publicNum(value));
  const currency = invoice.currency || 'NOK';

  const acceptOffer = async () => {
    // Ikke fall tilbake pa route.token (publicViewToken). Backend godtar bare
    // akseptttokenet, og det leveres kun for et tilbud som faktisk kan aksepteres.
    const acceptanceToken = invoice.offerAcceptanceToken || '';
    if (!route || !acceptanceToken) {
      setAcceptedMessage('');
      setState((current) => ({ ...current, error: t('r1e.pub.offerMissingData') }));
      return;
    }
    setAccepting(true);
    setAcceptedMessage('');
    try {
      const payload = await invoicePublicPost('acceptOfferHttp', {
        businessId: route.businessId,
        invoiceId: route.invoiceId,
        token: acceptanceToken,
      });
      setAcceptedMessage(payload.alreadyAccepted ? t('r1e.pub.offerAlreadyAccepted') : t('r1e.pub.offerAccepted'));
      setState((current) => ({
        ...current,
        invoice: {
          ...current.invoice,
          offerAcceptedAt: payload.acceptedAt || new Date().toISOString(),
        },
      }));
    } catch (error) {
      setState((current) => ({ ...current, error: error?.message || t('r1e.pub.offerAcceptFailed') }));
    } finally {
      setAccepting(false);
    }
  };

  return <div style={{ minHeight: '100vh', background: '#F7F4FB', color: '#211733', fontFamily: 'system-ui,-apple-system,Segoe UI,Roboto,sans-serif' }}>
    <main style={{ width: 'min(980px, calc(100% - 32px))', margin: '0 auto', padding: '28px 0 40px' }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#6D28B5' }}>Plumme</div>
          <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.15 }}>{state.loading ? t('r1e.pub.opening') : `${documentLabel} ${invoice.invoiceNumber || ''}`}</h1>
        </div>
        {isOffer && !state.loading && <button type="button" disabled={accepting || alreadyAccepted} onClick={acceptOffer} style={{ border: 0, borderRadius: 'var(--r-xs)', padding: '12px 16px', background: alreadyAccepted ? '#DDF6E7' : '#6D28B5', color: alreadyAccepted ? '#13683A' : '#fff', fontWeight: 800, cursor: accepting || alreadyAccepted ? 'default' : 'pointer' }}>
          {alreadyAccepted ? 'Akseptert' : accepting ? 'Aksepterer...' : 'Aksepter tilbud'}
        </button>}
      </header>

      {state.error && <div style={{ border: '1px solid #F3B7B7', background: '#FFF1F1', color: '#8A1F1F', borderRadius: 'var(--r-xs)', padding: 14, marginBottom: 16, fontWeight: 700 }}>{state.error}</div>}
      {acceptedMessage && <div style={{ border: '1px solid #A9E5BF', background: '#F0FFF5', color: '#13683A', borderRadius: 'var(--r-xs)', padding: 14, marginBottom: 16, fontWeight: 800 }}>{acceptedMessage}</div>}

      {state.loading && <section style={{ background: '#fff', border: '1px solid #E7DFF2', borderRadius: 'var(--r-xs)', padding: 22, fontWeight: 700 }}>{t('r1e.pub.loading')}</section>}

      {!state.loading && invoice.id && <section style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
        <div style={{ background: '#fff', border: '1px solid #E7DFF2', borderRadius: 'var(--r-xs)', padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 18 }}>
            <Info label={t('sharedText.km.from')} value={settings.businessName || 'Plumme'} />
            <Info label={t('sharedText.km.to')} value={invoice.customer?.name || ''} />
            <Info label={doc.secondaryLabel} value={doc.secondaryValue} />
            <Info label={doc.dateLabel} value={(invoice.date || '').slice(0, 10)} />
            {!isOffer && !isCredit && <Info label={t('sharedText.set.accountNo')} value={invoice.bankAccount || ''} />}
            {!isOffer && !isCredit && invoice.showIbanBicOnInvoice === true && invoice.bankAccountIban && <Info label="IBAN" value={invoice.bankAccountIban} />}
            {!isOffer && !isCredit && invoice.showIbanBicOnInvoice === true && invoice.bankAccountBic && <Info label="BIC" value={invoice.bankAccountBic} />}
          </div>
          {isCredit && invoice.creditReason && <p style={{ margin: '0 0 18px', color: '#665874', fontWeight: 650 }}>
            <b style={{ color: '#211733' }}>{t('sharedText.sale.credit.previewReason')}</b> {invoice.creditReason}
          </p>}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #E7DFF2' }}>
                  <th style={{ padding: '10px 8px' }}>{t('sharedText.clo.description')}</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right' }}>{t('r1e.pub.quantity')}</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right' }}>{t('sharedText.prd.price')}</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right' }}>{t('sharedText.vat.vat')}</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right' }}>{t('sharedText.clo.sum')}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const lineType = publicLineType(line);
                  if (lineType === 'spacer') return <tr key={line.id || index}><td colSpan={5} style={{ padding: '12px 8px' }}></td></tr>;
                  if (!publicLineHasAmount(line)) return <tr key={line.id || index} style={{ borderBottom: line.showDivider === false ? 'none' : '1px solid #F1ECF8' }}>
                    <td colSpan={5} style={{ padding: '11px 8px', whiteSpace: 'pre-wrap' }}>
                      <div style={{ fontWeight: line.nameStyle?.bold || (!line.nameStyle && line.textStyle?.bold) || line.bold ? 800 : 650, fontStyle: line.nameStyle?.italic || (!line.nameStyle && line.textStyle?.italic) || line.italic ? 'italic' : 'normal' }}>{line.description || ''}</div>
                      {line.note && <div style={{ marginTop: 3, color: '#7A6B90', fontSize: 13, fontWeight: line.textStyle?.bold ? 800 : 600, fontStyle: line.textStyle?.italic ? 'italic' : 'normal' }}>{line.note}</div>}
                    </td>
                  </tr>;
                  return <tr key={line.id || index} style={{ borderBottom: '1px solid #F1ECF8' }}>
                    <td style={{ padding: '11px 8px', fontWeight: 700 }}>{line.description || 'Linje'}</td>
                    <td style={{ padding: '11px 8px', textAlign: 'right' }}>{Math.abs(publicNum(line.quantity, 1))}</td>
                    <td style={{ padding: '11px 8px', textAlign: 'right' }}>{publicMoney(amountOf(line.documentUnitPrice ?? line.unitPrice), currency)}</td>
                    <td style={{ padding: '11px 8px', textAlign: 'right' }}>{publicNum(line.vatRate)}%</td>
                    <td style={{ padding: '11px 8px', textAlign: 'right', fontWeight: 800 }}>{publicMoney(amountOf(publicLineGross(line)), currency)}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'grid', justifyContent: 'end', gap: 6, marginTop: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 28 }}><span>{t('sharedText.inv.kpi.sub.exVat')}</span><b>{publicMoney(amountOf(netTotal), currency)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 28, fontSize: 20 }}><span>{doc.totalLabel}</span><b>{publicMoney(amountOf(grossTotal), currency)}</b></div>
          </div>
          {invoice.accountingCurrency && invoice.accountingCurrency !== currency && <div style={{ marginTop: 8, textAlign: 'right', color: '#7A6B90', fontSize: 13, fontWeight: 700 }}>{t('sale.preview.accountingAmount', { amount: publicMoney(invoice.totalGrossNok || grossTotal * publicNum(invoice.exchangeRate), invoice.accountingCurrency), currency: '', rate: invoice.exchangeRate })}</div>}
          {invoice.comment && <p style={{ margin: '18px 0 0', color: '#665874', fontWeight: 650 }}>{invoice.comment}</p>}
        </div>

        {pdfUrl && <div style={{ background: '#fff', border: '1px solid #E7DFF2', borderRadius: 'var(--r-xs)', padding: 12 }}>
          <iframe title={t('r1e.pub.pdfTitle')} src={pdfUrl} style={{ width: '100%', height: 'min(76vh, 780px)', border: 0, borderRadius: 'var(--r-2xs)' }} />
        </div>}
      </section>}
    </main>
  </div>;
}

function Info({ label, value }) {
  return <div>
    <div style={{ fontSize: 12, color: '#806F93', fontWeight: 800, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 800, marginTop: 3 }}>{value || '-'}</div>
  </div>;
}

InvoicePublicView.parseCurrentPath = parsePublicInvoicePath;
window.InvoicePublicView = InvoicePublicView;
